import { useEffect } from 'react';
import axios from 'axios';
import { useMutation, useQuery, useQueryClient } from 'react-query';

const apiUrl = () => process.env.REACT_APP_BASIX_API || '';

// item 318 — a teammate currently online (active within the server's 2-min window).
export interface OnlineUser {
  id: string;
  name: string;
  photoUrl?: string | null;
  lastSeen?: string;
}

// item 318 — presence heartbeat: bump my LastSeen every 45s while the app is open. Mount
// ONCE (App), gated on auth. Best-effort (failures are ignored — presence is non-critical).
export const useHeartbeat = (enabled: boolean): void => {
  useEffect(() => {
    if (!enabled) return undefined;
    let cancelled = false;
    const beat = () => { axios.post(`${apiUrl()}Presence/heartbeat`).catch(() => undefined); };
    beat();
    const id = setInterval(() => { if (!cancelled) beat(); }, 45000);
    return () => { cancelled = true; clearInterval(id); };
  }, [enabled]);
};

// item 318 — who's online now; polled for the header avatar row.
export const useOnlineUsers = () => useQuery<OnlineUser[]>(
  'onlineUsers',
  async () => {
    const { data } = await axios.get(`${apiUrl()}Presence/online`);
    return data;
  },
  { refetchInterval: 30000 },
);

// item 318 — set a profile picture (a data URL; null clears it). Pass a userId to set
// ANOTHER user's photo (admin-only, enforced server-side); omit it for your own.
export const useSetPhoto = (userId?: string) => {
  const qc = useQueryClient();
  const mutation = useMutation(
    (photoUrl: string | null) => axios.post(`${apiUrl()}Presence/photo`, { photoUrl, userId: userId || undefined }),
    {
      onSuccess: () => {
        qc.invalidateQueries('onlineUsers');
        qc.invalidateQueries('users');
        qc.invalidateQueries('user');
      },
    },
  );
  return { setPhoto: (p: string | null) => mutation.mutateAsync(p), isSaving: mutation.isLoading };
};
